/**
 * Google Ads Conversion Tracking Utility
 * Handles all conversion tracking events for the website
 */

/* global gtag */

// Conversion IDs from Google Ads account (provided by client)
const CONVERSION_IDS = {
  PHONE_CLICK: 'AW-18059162501/iVamCIzPyJQcEIXno6ND', // Phone click conversions (Click to call)
  WHATSAPP_CLICK: 'AW-18059162501/7vkFCKu60pQcEIXno6ND', // WhatsApp click conversions
};

// Export CONVERSION_IDS for use in components
export { CONVERSION_IDS };

/**
 * Track a phone click conversion
 * @param {string} phoneNumber - The phone number being clicked
 * @param {string} eventLabel - Label for the conversion (e.g., 'Hero Call Button', 'Contact Section')
 */
export const trackPhoneClick = (phoneNumber, eventLabel = 'Phone Click') => {
  if (typeof window !== 'undefined' && window.gtag) {
    gtag('event', 'conversion', {
      'send_to': CONVERSION_IDS.PHONE_CLICK,
      'value': 1.0,
      'currency': 'INR',
      'event_label': eventLabel,
      'phone_number': phoneNumber,
    });
    console.log('📞 Phone click conversion tracked:', eventLabel, phoneNumber);
  } else {
    console.log('⚠️ gtag not available - tracking skipped for:', eventLabel);
  }
};

/**
 * Track a WhatsApp click conversion
 * @param {string} eventLabel - Label for the conversion (e.g., 'Hero WhatsApp', 'Footer WhatsApp')
 */
export const trackWhatsAppClick = (eventLabel = 'WhatsApp Click') => {
  if (typeof window !== 'undefined' && window.gtag) {
    gtag('event', 'conversion', {
      'send_to': CONVERSION_IDS.WHATSAPP_CLICK,
      'value': 1.0,
      'currency': 'INR',
      'event_label': eventLabel,
    });
    console.log('💬 WhatsApp click conversion tracked:', eventLabel);
  } else {
    console.log('⚠️ gtag not available - tracking skipped for:', eventLabel);
  }
};

/**
 * Track a form submission conversion
 * @param {string} formType - Type of form ('parent_registration' or 'tutor_registration')
 * @param {string} conversionValue - Value of the conversion in INR (default 1.0)
 */
export const trackFormSubmission = (formType, conversionValue = 1.0) => {
  if (typeof window !== 'undefined' && window.gtag) {
    gtag('event', 'conversion', {
      'send_to': CONVERSION_IDS.PHONE_CLICK, // Using phone conversion ID for form submissions
      'value': conversionValue,
      'currency': 'INR',
      'event_label': `Form Submission - ${formType}`,
    });
    console.log('📝 Form submission conversion tracked:', formType, 'Value:', conversionValue, 'INR');
  } else {
    console.log('⚠️ gtag not available - tracking skipped for form submission');
  }
};

/**
 * Track page view with custom label
 * @param {string} pageName - Name/path of the page
 * @param {string} eventLabel - Optional additional label
 */
export const trackPageView = (pageName, eventLabel = '') => {
  if (typeof window !== 'undefined' && window.gtag) {
    gtag('event', 'page_view', {
      'page_title': pageName,
      'event_label': eventLabel,
    });
    console.log('📄 Page view tracked:', pageName);
  } else {
    console.log('⚠️ gtag not available - page view tracking skipped');
  }
};

/**
 * Track email contact (if available)
 * @param {string} email - Email address
 */
export const trackEmailInteraction = (email) => {
  if (typeof window !== 'undefined' && window.gtag) {
    gtag('event', 'conversion', {
      'send_to': CONVERSION_IDS.PHONE_CLICK, // Using phone conversion ID for email
      'value': 1.0,
      'currency': 'INR',
      'event_label': 'Email Contact',
      'email': email,
    });
    console.log('📧 Email contact conversion tracked:', email);
  } else {
    console.log('⚠️ gtag not available - email tracking skipped');
  }
};

/**
 * Report a conversion and navigate to URL
 * Used when you want to track AND navigate (like for phone calls)
 * Follows the client's gtag_report_conversion pattern
 * @param {string} url - URL to navigate to after tracking
 * @param {string} conversionId - Conversion ID to use (e.g., CONVERSION_IDS.PHONE_CLICK or CONVERSION_IDS.WHATSAPP_CLICK)
 * @param {string} eventLabel - Label for the conversion
 * @param {number} conversionValue - Value of the conversion (default 1.0)
 */
export const gtag_report_conversion = (url, conversionId = CONVERSION_IDS.PHONE_CLICK, eventLabel = 'Conversion', conversionValue = 1.0) => {
  // Add a delay to allow gtag to process the conversion before navigating
  const callback = function () {
    // Small delay (100ms) to ensure gtag sends the event
    setTimeout(() => {
      if (typeof url !== 'undefined') {
        window.location = url;
      }
    }, 100);
  };

  if (typeof window !== 'undefined' && window.gtag) {
    gtag('event', 'conversion', {
      'send_to': conversionId,
      'value': conversionValue,
      'currency': 'INR',
      'event_label': eventLabel,
      'event_callback': callback
    });
    console.log('📞 Conversion tracked:', eventLabel, 'with ID:', conversionId);
  } else {
    // If gtag is not available, still navigate after delay
    setTimeout(() => {
      if (typeof url !== 'undefined') {
        window.location = url;
      }
    }, 100);
    console.warn('⚠️ gtag not available - navigation without conversion tracking');
  }
  return false;
};

export default {
  CONVERSION_IDS,
  trackPhoneClick,
  trackWhatsAppClick,
  trackFormSubmission,
  trackPageView,
  trackEmailInteraction,
  gtag_report_conversion,
};
